/**
 * RuleDeleteConfirm — delete confirmation dialog for gate rules.
 * US-006 Step 5: destructive confirmation showing rule name and severity.
 * WCAG 2.1 AA: focus trap via Radix Dialog, aria-describedby on confirm button.
 */
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useDeleteRule, type GateRuleResponse } from '@/hooks/useGateRules';

const SEVERITY_STYLES: Record<string, string> = {
  blocking: 'border-transparent bg-destructive text-destructive-foreground',
  warning: 'border-transparent bg-amber-500 text-white',
  info: 'border-transparent bg-blue-500 text-white',
};

const SEVERITY_LABELS: Record<string, string> = {
  blocking: 'Blocking',
  warning: 'Warning',
  info: 'Info',
};

interface RuleDeleteConfirmProps {
  rule: GateRuleResponse | null;
  onClose: () => void;
}

export function RuleDeleteConfirm({ rule, onClose }: RuleDeleteConfirmProps) {
  const { mutate: deleteRule, isPending } = useDeleteRule();
  const descId = 'rule-delete-description';

  const handleConfirm = () => {
    if (!rule) return;
    deleteRule(rule.id, { onSuccess: onClose });
  };

  return (
    <AlertDialog
      open={!!rule}
      onOpenChange={(open) => { if (!open) onClose(); }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Rule</AlertDialogTitle>
          <AlertDialogDescription id={descId}>
            Are you sure you want to delete{' '}
            <strong className="text-foreground">&ldquo;{rule?.rule_name}&rdquo;</strong>?
            {' '}This action cannot be undone.
          </AlertDialogDescription>
          {rule && (
            <div className="mt-2 flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Severity:</span>
              <Badge className={cn('text-xs', SEVERITY_STYLES[rule.severity])}>
                {SEVERITY_LABELS[rule.severity] ?? rule.severity}
              </Badge>
            </div>
          )}
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className={cn(buttonVariants({ variant: 'destructive' }))}
            onClick={handleConfirm}
            disabled={isPending}
            aria-describedby={descId}
            aria-busy={isPending}
          >
            {isPending ? 'Deleting…' : 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
